import { useEffect, useMemo } from "react";
import { SimpleModal } from "../SimpleModal/SimpleModal";
import "./d3.basketball-shot-chart.css";
import "../../../TeamReport/DetailModal/DetailModal.scss";
import { Theme, useMediaQuery } from "@mui/material";
import { ShotChartData } from "../../../models/TableData";
import { useExplorerStore } from "../../../data/ExplorerStore";
import { ExplorerEnvironment } from "../../../data/Cache";
import useBasketballShotChart from "./useBasketballShotChart";

interface Props {
  data: ShotChartData;
  isOpen: boolean;
  handleClose: (event: object, reason: string) => void;
  title?: string;
}

function parseShotChartData(data: ShotChartData) {
  try {
    return JSON.parse(
      data.replace(/(["'])?([a-z0-9A-Z_]+)(["'])?:/g, '"$2": '),
    );
  } catch (e) {
    console.error("Failed to parse shot chart data:", e);
    return [];
  }
}

/**
 * Inner component that uses the hook. Mounts only when the dialog is open
 * so the hook's useEffect cleanly initializes/tears down with the dialog lifecycle.
 */
function ShotChartContent({
  data,
  width,
  showCollegeLine,
}: {
  data: ShotChartData;
  width: number;
  showCollegeLine: boolean;
}) {
  const chartData = useMemo(() => parseShotChartData(data), [data]);

  const { svgRef, draw } = useBasketballShotChart({
    width,
    showCollegeLine,
  });

  useEffect(() => {
    if (chartData.length > 0) {
      draw(chartData);
    }
  }, [chartData, draw]);

  return <svg ref={svgRef} />;
}

export default function ShotChartDialog({
  data,
  isOpen,
  handleClose,
  title,
}: Props) {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const environment = useExplorerStore((state) => state.environment);
  const showCollegeLine = environment === ExplorerEnvironment.DRAFT;
  const chartWidth = isMobile ? 300 : 770;

  if (!isOpen) return null;

  return (
    <SimpleModal
      isOpen={isOpen}
      handleClose={handleClose}
      maxWidth="md"
      title={title}
      dialogClassName="detail-modal--dialog"
    >
      <ShotChartContent
        data={data}
        width={chartWidth}
        showCollegeLine={showCollegeLine}
      />
    </SimpleModal>
  );
}
